import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { Box, Tabs, Tab, Typography, IconButton, useTheme } from '@mui/material';
import { DATERANGE, DateRangeType } from '_constants';
import dayjs from 'dayjs';
import { noop } from 'lodash';
import React, { useState } from 'react';

import { DateRangeSelectorProps } from './types';

export const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  onChange,
  onNotesClick = noop,
  initSelectedRange = DATERANGE.month,
  hideNotes = false,
}) => {
  const theme = useTheme();
  const [range, setRange] = useState<DateRangeType>(initSelectedRange);
  const [hideDates, setHideDates] = useState(false);
  const [startDate, setStartDate] = useState<string>(
    dayjs().startOf('month').format('YYYY-MM-DD'),
  );
  const [endDate, setEndDate] = useState<string>(
    dayjs().endOf('month').format('YYYY-MM-DD'),
  );

  React.useEffect(() => onNotesClick(hideDates), [hideDates]);

  const handleRangeChange = (event: React.SyntheticEvent, newValue: DateRangeType) => {
    setRange(newValue);
    updateDates(newValue);
  };

  const updateDates = (range: DateRangeType) => {
    if (range === 'notes') {
      setHideDates(true);
    } else {
      setHideDates(false);
    }
    let newStartDate;
    let newEndDate;
    switch (range) {
      case 'week':
        newStartDate = dayjs().startOf('week').format('YYYY-MM-DD');
        newEndDate = dayjs().endOf('week').format('YYYY-MM-DD');
        break;
      case 'month':
        newStartDate = dayjs().startOf('month').format('YYYY-MM-DD');
        newEndDate = dayjs().endOf('month').format('YYYY-MM-DD');
        break;
      case 'year':
        newStartDate = dayjs().startOf('year').format('YYYY-MM-DD');
        newEndDate = dayjs().endOf('year').format('YYYY-MM-DD');
        break;
      case 'notes':
        newStartDate = dayjs().startOf('month').format('YYYY-MM-DD');
        newEndDate = dayjs().endOf('month').format('YYYY-MM-DD');
        break;
      default:
        newStartDate = dayjs().startOf('week').format('YYYY-MM-DD');
        newEndDate = dayjs().endOf('week').format('YYYY-MM-DD');
    }
    onChange({ startDate: newStartDate, endDate: newEndDate });
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  const handlePrevClick = () => {
    let newStartDate;
    let newEndDate;
    switch (range) {
      case 'week':
        newStartDate = dayjs(startDate).subtract(1, 'week').format('YYYY-MM-DD');
        newEndDate = dayjs(endDate).subtract(1, 'week').format('YYYY-MM-DD');
        break;
      case 'month':
        newStartDate = dayjs(startDate)
          .subtract(1, 'month')
          .startOf('month')
          .format('YYYY-MM-DD');
        newEndDate = dayjs(endDate)
          .subtract(1, 'month')
          .endOf('month')
          .format('YYYY-MM-DD');
        break;
      case 'year':
        newStartDate = dayjs(startDate).subtract(1, 'year').format('YYYY-MM-DD');
        newEndDate = dayjs(endDate).subtract(1, 'year').format('YYYY-MM-DD');
        break;
      default:
        newStartDate = dayjs(startDate).subtract(1, 'week').format('YYYY-MM-DD');
        newEndDate = dayjs(endDate).subtract(1, 'week').format('YYYY-MM-DD');
    }
    onChange({ startDate: newStartDate, endDate: newEndDate });
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  const handleNextClick = () => {
    let newStartDate;
    let newEndDate;
    switch (range) {
      case 'week':
        newStartDate = dayjs(startDate).add(1, 'week').format('YYYY-MM-DD');
        newEndDate = dayjs(endDate).add(1, 'week').format('YYYY-MM-DD');
        break;
      case 'month':
        newStartDate = dayjs(startDate)
          .add(1, 'month')
          .startOf('month')
          .format('YYYY-MM-DD');
        newEndDate = dayjs(endDate).add(1, 'month').endOf('month').format('YYYY-MM-DD');
        break;
      case 'year':
        newStartDate = dayjs(startDate).add(1, 'year').format('YYYY-MM-DD');
        newEndDate = dayjs(endDate).add(1, 'year').format('YYYY-MM-DD');
        break;
      default:
        newStartDate = dayjs(startDate).add(1, 'week').format('YYYY-MM-DD');
        newEndDate = dayjs(endDate).add(1, 'week').format('YYYY-MM-DD');
    }
    onChange({ startDate: newStartDate, endDate: newEndDate });
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
      }}
    >
      <Tabs
        value={range}
        onChange={handleRangeChange}
        variant="fullWidth"
        sx={{ marginBottom: 2, width: '100%' }}
      >
        <Tab label="Week" value={DATERANGE.week} />
        <Tab label="Month" value={DATERANGE.month} />
        <Tab label="Year" value={DATERANGE.year} />
        {!hideNotes ? <Tab label="Notes" value={DATERANGE.notes} /> : null}
      </Tabs>
      {!hideDates ? (
        <Box
          sx={{
            alignItems: 'center',
            backgroundColor: theme.palette.primary.main,
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            padding: 5,
            width: '100%',
          }}
        >
          <IconButton onClick={handlePrevClick} sx={{ color: 'white' }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6">
            {dayjs(startDate).format('MMMM D, YYYY')} -{' '}
            {dayjs(endDate).format('MMMM D, YYYY')}
          </Typography>
          <IconButton onClick={handleNextClick} sx={{ color: 'white' }}>
            <ArrowForward />
          </IconButton>
        </Box>
      ) : null}
    </Box>
  );
};
